import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { createStackNavigator } from "@react-navigation/stack";
import { UserStackParamList } from "../types";
import React, { useContext, useEffect, useState } from 'react';
import UserList from "../screens/MidLevel/UsersScreen/Components/userList";
import UserScreen from '../screens/MidLevel/UsersScreen/UsersScreen';
import UserActivity from "../screens/MidLevel/UsersScreen/UserActivity";
import StartupBuilder from "@navigation/screens/MidLevel/UsersScreen/StartupBuilder";
import CompanySettings from "@navigation/screens/MidLevel/SettingsScreens/CompanySettings";
import HRManagement from "@navigation/screens/MidLevel/HRScreen/HRManagement";
import UserMenu from "@navigation/screens/MidLevel/HRScreen/UserMenu";
import HRDashboard from "@navigation/screens/MidLevel/HRScreen/HRDashboard";
import EmployeeManagement from "@navigation/screens/MidLevel/HRScreen/EmployeeManagement";
import LeaveManagement from "@navigation/screens/MidLevel/HRScreen/LeaveManagement";
import AttendanceManagement from "@navigation/screens/MidLevel/HRScreen/AttendanceManagement";
import PayrollManagement from "@navigation/screens/MidLevel/HRScreen/PayrollManagement";
import PerformanceReviews from "@navigation/screens/MidLevel/HRScreen/PerformanceReviews";
import { useDispatch, useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";
import { ToolbarContext } from "@hooks/toolbarContext";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { AntDesign, Feather, FontAwesome6, MaterialCommunityIcons } from "@expo/vector-icons";
import { SHOW_AUTH_MODAL, TOGGLE_GLOBAL_HIERARCHY } from "@reduxLocal/actions/types";
import { View, Image, Text, Pressable, Platform } from "react-native";
import { Avatar, AvatarGroup } from "@constants/Themed";


const ConnectedHeader = () => {
  const theme = themeSelector();
  const { hierarchySocket } = useContext(ToolbarContext);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (hierarchySocket?.connected) {
      setConnected(true);
    } else {
      hierarchySocket?.connect();
    }
  }, [hierarchySocket])

  return (
    <View style={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 8,
      paddingVertical: 4,
      backgroundColor: connected ? '#10b981' : '#ef4444',
      borderRadius: 6
    }}>
      <MaterialCommunityIcons
        name={connected ? "access-point-network" : "access-point-network-off"}
        size={16}
        color="white"
      />
      <Text style={{
        color: 'white',
        fontSize: 12,
        fontWeight: '500'
      }}>
        {connected ? 'Connected' : 'Offline'}
      </Text>
    </View>
  )
}

const GlobalView = () => {
  const dispatch = useDispatch()
  const showGlobal = useSelector((state: DefaultRootState) => state.UI.userHierarchyGlobal);
  const theme = themeSelector();

  return (
    <Pressable
      onPress={() => dispatch({ type: TOGGLE_GLOBAL_HIERARCHY })}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 4,
        backgroundColor: showGlobal ? '#3b82f6' : theme ? '#e5e7eb' : '#374151',
        borderRadius: 6
      }}
    >
      <AntDesign
        name="cloud-sync"
        size={16}
        color={showGlobal ? "white" : (theme ? "#6b7280" : "#9ca3af")}
      />
      <Text style={{
        color: showGlobal ? "white" : (theme ? "#6b7280" : "#9ca3af"),
        fontSize: 12,
        fontWeight: '500'
      }}>
        Global View
      </Text>
    </Pressable>
  )
}

const UserStack = Platform.OS === 'web'
  ? createStackNavigator<UserStackParamList>()
  : createNativeStackNavigator<UserStackParamList>()
const UserNavigator = ({ navigation }) => {
  const theme = themeSelector();

    return (
      <UserStack.Navigator
        initialRouteName="UserList"
        {...(Platform.OS === 'web' ? { screenOptions: { cardStyle: { backgroundColor: theme ? "white" : "black" } } as any } : {})}
      >
        <UserStack.Screen
          name="UserList"
          component={UserScreen}
          options={{
            freezeOnBlur: true,
            cardStyle: {
              backgroundColor: theme ? "white" : "black",
            },
            headerShown: Platform.OS === 'web',
            header(props) {
              const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
              const theme = themeSelector();
              const dispatch = useDispatch();
              const users = useSelector((state: DefaultRootState) => state.userHierarchy.usersInRoom);

              return (
                <View style={{
                  alignSelf: "stretch",
                  backgroundColor: theme ? 'white' : '#1a1a1a',
                  borderBottomWidth: 1,
                  borderEndStartRadius: 0,
                  borderEndEndRadius: 0,
                  borderBottomColor: theme ? '#e5e7eb' : '#374151',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 3,
                  paddingHorizontal: "5%",
                  marginHorizontal: '5%',
                  paddingVertical: 16,
                  overflow: 'hidden',
                  minHeight: 80
                }}>
                  <View style={{
                    flexDirection: "row",
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flex: 1
                  }}>
                    {/* Left Section - Title and Status */}
                    <View style={{ flexDirection: "row", alignItems: 'center', gap: 16, flex: 1 }}>
                      <Text style={{
                        fontSize: 24,
                        fontWeight: '600',
                        color: theme ? '#111827' : '#f9fafb',
                        letterSpacing: -0.5
                      }}>
                        User Management
                      </Text>

                      <View style={{ flexDirection: "row", alignItems: 'center', gap: 12 }}>
                        <GlobalView />
                        <ConnectedHeader />
                      </View>
                    </View>

                    {/* Center Section - Active Users */}
                    {users.length > 1 && (
                      <View style={{
                        flexDirection: "row",
                        alignItems: 'center',
                        gap: 8,
                        backgroundColor: theme ? '#f3f4f6' : '#374151',
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 8
                      }}>
                        <Text style={{
                          fontSize: 12,
                          fontWeight: '500',
                          color: theme ? '#6b7280' : '#9ca3af',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}>
                          Active Users
                        </Text>
                        <AvatarGroup
                          max={5}
                          spacing={-8}
                          avatars={users.map((user) => ({
                            src: user.profile_information?.avatar?.img || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png",
                            alt: `${user.profile_information?.name || ''} ${user.profile_information?.surname || ''}`.trim() || 'User'
                          }))}
                        />
                      </View>
                    )}

                    {/* Right Section - Action Buttons */}
                    <View style={{ flexDirection: "row", gap: 12 }}>
                      <Pressable
                        onPress={() => {
                          if (currentCompany?.id) {
                            dispatch({ type: TOGGLE_GLOBAL_HIERARCHY })
                          } else {
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Account must be a company account to manage users.", type: 'error' } })
                          }
                        }}
                        style={({ pressed }) => [
                          { 
                            flexDirection: "row",
                            alignItems: 'center',
                            gap: 8,
                            backgroundColor: '#3b82f6',
                            paddingHorizontal: 16,
                            paddingVertical: 10,
                            borderRadius: 8,
                            shadowColor: '#3b82f6',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.2,
                            shadowRadius: 4,
                            elevation: 2,
                            opacity: pressed ? 0.9 : 1,
                            transform: [{ scale: pressed ? 0.98 : 1 }]
                          }
                        ]}
                      >
                        {currentCompany && currentCompany.logo ? (
                          <Image
                            source={{ uri: currentCompany.logo }}
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 4,
                              resizeMode: "contain"
                            }}
                          />
                        ) : (
                          <MaterialCommunityIcons
                            name="office-building-outline"
                            size={20}
                            color="white"
                          />
                        )}
                        <Text style={{
                          color: "white",
                          fontWeight: "600",
                          fontSize: 14
                        }}>
                          Company
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={() => {
                          if (currentCompany?.id) {
                            dispatch({ type: TOGGLE_GLOBAL_HIERARCHY })
                          } else {
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Account must be a company account to manage users.", type: 'error' } })
                          }
                        }}
                        style={({ pressed }) => [
                          { 
                            flexDirection: "row",
                            alignItems: 'center',
                            gap: 8,
                            backgroundColor: '#f59e0b',
                            paddingHorizontal: 16,
                            paddingVertical: 10,
                            borderRadius: 8,
                            shadowColor: '#f59e0b',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.2,
                            shadowRadius: 4,
                            elevation: 2,
                            opacity: pressed ? 0.9 : 1,
                            transform: [{ scale: pressed ? 0.98 : 1 }]
                          }
                        ]}
                      >
                        <Feather name="users" size={20} color="white" />
                        <Text style={{
                          color: "white",
                          fontWeight: "600",
                          fontSize: 14
                        }}>
                          Members
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={() => {
                          if (currentCompany?.id) {
                            dispatch({ type: TOGGLE_GLOBAL_HIERARCHY })
                          } else {
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Account must be a company account to manage users.", type: 'error' } })
                          }
                        }}
                        style={({ pressed }) => [
                          {
                            flexDirection: "row",
                            alignItems: 'center',
                            gap: 8,
                            backgroundColor: '#ef4444',
                            paddingHorizontal: 16,
                            paddingVertical: 10,
                            borderRadius: 8,
                            shadowColor: '#ef4444',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.2,
                            shadowRadius: 4,
                            elevation: 2,
                            opacity: pressed ? 0.9 : 1,
                            transform: [{ scale: pressed ? 0.98 : 1 }]
                          }
                        ]}
                      >
                        <FontAwesome6 name="users-rectangle" size={18} color="white" />
                        <Text style={{
                          color: "white",
                          fontWeight: "600",
                          fontSize: 14
                        }}>
                          Temporary
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                </View>
              )
            },
          }}
        />
        <UserStack.Screen
          name="StartupBuilder"
          options={{ title: "Startup Builder", headerShown: false }}
          component={StartupBuilder} />
        <UserStack.Screen
          name="UserActivity"
          options={{ title: "Add Users", headerShown: false }}
          component={UserActivity} />
        <UserStack.Screen
          name="CompanySettings"
          options={{ title: "Company Settings", headerShown: false }}
          component={CompanySettings} />
        {Platform.OS === 'web' ? (
          <UserStack.Screen
            name="HRManagement"
            options={{ title: "HR Management", headerShown: false }}
            component={HRManagement} />
        ) : (
          <>
            <UserStack.Screen
              name="UserMenu"
              options={{ title: "HR Menu", headerShown: false }}
              component={UserMenu} />
            <UserStack.Screen
              name="HRDashboard"
              options={{ title: "HR Dashboard", headerShown: false }}
              component={HRDashboard} />
            <UserStack.Screen
              name="EmployeeManagement"
              options={{ title: "Employee Management", headerShown: false }}
              component={EmployeeManagement} />
            <UserStack.Screen
              name="LeaveManagement"
              options={{ title: "Leave Management", headerShown: false }}
              component={LeaveManagement} />
            <UserStack.Screen
              name="AttendanceManagement"
              options={{ title: "Attendance Management", headerShown: false }}
              component={AttendanceManagement} />
            <UserStack.Screen
              name="PayrollManagement"
              options={{ title: "Payroll Management", headerShown: false }}
              component={PayrollManagement} />
            <UserStack.Screen
              name="PerformanceReviews"
              options={{ title: "Performance Reviews", headerShown: false }}
              component={PerformanceReviews} />
          </>
        )}
      </UserStack.Navigator>
    )
  }

export { UserStack, UserNavigator };